import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
// import Chip from '@mui/material/Chip';
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { LineChart } from "@mui/x-charts/LineChart";

import { Client, ClientTypes, RevenueChartTypes } from "../../../utils/types";
import { useQuery } from "@tanstack/react-query";
import { fetchClients } from "../../../utils/helper";

import { useAuth } from "../../../context/AuthContext";

export type RevenuChartProps = {
  type: RevenueChartTypes;
  client?: Client;
};

// This creates the color gradient for the below chart
function AreaGradient({ color, id }: { color: string; id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default function RevenueChart(props: RevenuChartProps) {
  const { type, client } = props;

  const { jwt } = useAuth();
  
// Fetch clients from React Query cache
const { data: clients = [] } = useQuery({
  queryKey: ['clients'],
  queryFn: () => fetchClients(jwt),
});

// Aggregate revenue by type
  const atmRevenue = clients
    .filter(client => client.type === ClientTypes.ATM)
    .reduce((acc, client) => {
      return acc.map((sum, idx) => sum + (client.revenue[idx] || 0));
  }, new Array(12).fill(0));

  const airbnbRevenue = clients
    .filter(client => client.type === ClientTypes.AIRBNB)
    .reduce((acc, client) => {
      return acc.map((sum, idx) => sum + (client.revenue[idx] || 0));
  }, new Array(12).fill(0));

  const vendingRevenue = clients
  .filter(client => client.type === ClientTypes.VENDING)
  .reduce((acc, client) => {
    return acc.map((sum, idx) => sum + (client.revenue[idx] || 0));
  }, new Array(12).fill(0));

  const totalClients = clients.length;
  const totalRevenue = atmRevenue.reduce((sum, val) => sum + (val || 0), 0) +
    airbnbRevenue.reduce((sum, val) => sum + (val || 0), 0) +
    vendingRevenue.reduce((sum, val) => sum + (val || 0), 0);
  const averageRevenuePlus2000 = totalClients > 0 ? totalRevenue / (totalClients * 12) + 2000 : 0;

  const theme = useTheme();

  const colorPalette = [
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
  ];

  const avgRevenuePlus500 =
    client?.revenue && Array.isArray(client.revenue)
      ? client.revenue.filter((n) => n !== 0).reduce((a, b) => a + b, 0) /
          (client.revenue.filter((n) => n !== 0).length || 1) +
        500
      : 1000;

  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          Revenue
        </Typography>
        <Stack sx={{ justifyContent: "space-between" }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="h4" component="p">
              $
              {type === RevenueChartTypes.CLIENT
                ? client?.revenue?.reduce((a, b) => a + b, 0) ?? 0
                : atmRevenue.reduce((a, b) => a + b, 0) +
                  vendingRevenue.reduce((a, b) => a + b, 0) +
                  airbnbRevenue.reduce((a, b) => a + b, 0)}
            </Typography>
            {/* This will be the monthyl addative from the previous month. may or may not use */}
            {/* <Chip size="small" color="success" label={`+${1}%`} /> */}
          </Stack>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Year to date revenue
          </Typography>
        </Stack>
        <LineChart
          colors={colorPalette}
          xAxis={[
            {
              scaleType: "point",
              data: months,
              height: 24,
            },
          ]}
          yAxis={[{ width: 50, max: client ? avgRevenuePlus500 : averageRevenuePlus2000 }]}
          series={
            type === RevenueChartTypes.CLIENT
              ? [
                  {
                    id: "client",
                    label: "Client",
                    showMark: false,
                    curve: "linear",
                    area: true,
                    data: Array.isArray(client?.revenue) ? client?.revenue : [],
                  },
                ]
              : [
                  {
                    id: "atm",
                    label: "ATM's",
                    showMark: false,
                    curve: "linear",
                    area: true,
                    data: atmRevenue,
                  },
                  {
                    id: "vending",
                    label: "Vending",
                    showMark: false,
                    curve: "linear",
                    area: true,
                    data: vendingRevenue,
                  },
                  {
                    id: "airBnb",
                    label: "Air BnB",
                    showMark: false,
                    curve: "linear",
                    area: true,
                    data: airbnbRevenue,
                  },
                ]
          }
          height={250}
          margin={{ left: 0, right: 20, top: 20, bottom: 0 }}
          grid={{ horizontal: true }}
          sx={
            type === RevenueChartTypes.CLIENT
              ? {
                  "& .MuiAreaElement-series-client": {
                    fill: "url('#client')",
                  },
                }
              : {
                  "& .MuiAreaElement-series-airBnb": {
                    fill: "url('#airBnb')",
                  },
                  "& .MuiAreaElement-series-vending": {
                    fill: "url('#vending')",
                  },
                  "& .MuiAreaElement-series-atm": {
                    fill: "url('#atm')",
                  },
                }
          }
          hideLegend
        >
          {type === RevenueChartTypes.CLIENT ? (
            <AreaGradient color={theme.palette.primary.dark} id="client" />
          ) : (
            <>
              <AreaGradient color={theme.palette.primary.dark} id="airBnb" />
              <AreaGradient color={theme.palette.primary.main} id="vending" />
              <AreaGradient color={theme.palette.primary.light} id="atm" />
            </>
          )}
        </LineChart>
      </CardContent>
    </Card>
  );
}
